import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Decimal } from 'decimal.js';
import { compact } from 'lodash-es';
import { Controller, useFormContext } from 'react-hook-form';
import { TextField, Typography, WithStatusIcon } from 'seagull';
import { ServiceFormDataType } from '~/components/BillClientModal/BillClientModal.types';
import BillingItemNote from '~/components/BillClientModal/BillingNote/BillingItemNote';
import { ServiceEffectiveDateCallout, ServicePendingAmendmentCallout } from '~/components/BillClientModal/Callouts/Callouts';
import CumulativeWarningCallout from '~/components/BillClientModal/Callouts/CumulativeWarningCallout';
import { useLocaleKeys } from '~/i18n';
import { serviceCostModel } from '~/models';
import { BillingStageType } from '~/models/manual-service-billing';
import { costDisplayTextsPath } from '~/models/service-cost/selectors';
import { utils } from '~/utils';
import { connectControllerToTextField } from '~/utils/react-hook-form.utils';
import styles from '../BillClientModal.module.scss';
import BillTableItem from '../BillTableItem/BillTableItem';
import { billClientModalTextsPath, calculateTotalFormQuantity, getQuantityWarning, getTableDefs, getTotal, qtyWarningToText } from './BillServiceTableItem.utils';
const maxQuantity = new Decimal(999999);
const maxPrice = new Decimal(999999);
const maxError = 'max';
const requiredError = 'required';
export default function BillServiceTableItem(props) {
    const { t: billTexts } = useLocaleKeys(billClientModalTextsPath);
    const { t: costDisplayTexts } = useLocaleKeys(costDisplayTextsPath);
    const { control, watch } = useFormContext();
    const [items, issueDate, mode] = watch([
        'items',
        'issueDate',
        'mode'
    ]);
    const { manualServiceBilling, actionColumn, itemIndex, serviceItem, relationshipEffectiveDate } = props;
    const { price: priceInput, quantity: quantityInput, cost: costInput } = serviceItem.service;
    const service = serviceItem.service;
    const fullService = serviceItem.fullService;
    const cost = useMemo(()=>{
        if (service.type === ServiceFormDataType.Stages) {
            const specificStage = service.billingStages.find((stage)=>stage.stageNumber === service.stageNumber);
            if (specificStage) return _object_spread_props(_object_spread({}, costInput), {
                price: specificStage.amount
            });
            return _object_spread_props(_object_spread({}, costInput), {
                price: service.billingStages.filter((stage)=>stage.type === BillingStageType.Available)[0].amount
            });
        }
        return costInput;
    }, [
        service,
        costInput
    ]);
    const quantity = useMemo(()=>quantityInput ? new Decimal(+quantityInput) : new Decimal(0), [
        quantityInput
    ]);
    const total = useMemo(()=>getTotal(fullService, service, quantity, priceInput), [
        quantity,
        priceInput,
        fullService,
        service
    ]);
    const { columnDefs, headerItems } = useMemo(()=>getTableDefs({
            service: fullService,
            tableHeaders: billTexts.tableHeaders
        }), [
        fullService
    ]);
    const totalServiceQuantity = calculateTotalFormQuantity(items, fullService.id);
    const quantityWarning = getQuantityWarning({
        service: fullService,
        quantity: totalServiceQuantity,
        issueDate,
        manualServiceBilling,
        relationshipEffectiveDate
    });
    const isVariablePrice = serviceCostModel.isVariablePrice(cost);
    const isRangePrice = serviceCostModel.isRangePrice(cost);
    const getRangeController = (cost)=>/*#__PURE__*/ _jsx(Controller, {
            control: control,
            name: "items.".concat(itemIndex, ".service.price"),
            rules: {
                validate: (value)=>{
                    if (!value) return requiredError;
                    const asNumber = new Decimal(+value);
                    if (asNumber.greaterThan(maxPrice)) return maxError;
                }
            },
            render: (renderProps)=>{
                const { field, fieldState: { error } } = renderProps;
                const isWarning = field.value !== '' && cost.maxPrice.lessThan(field.value);
                return /*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                    bi: {
                        name: 'service_rate'
                    },
                    collapseHelperText: false,
                    isMoney: true
                }, connectControllerToTextField(renderProps, {
                    noHelperText: true,
                    alwaysShowErrors: true
                })), {
                    width: 130,
                    inputProps: {
                        min: 0,
                        className: styles.amountCell
                    },
                    testId: "bill-service-".concat(itemIndex, "-table-rate"),
                    warning: isWarning && !error,
                    suffix: (error || isWarning) && /*#__PURE__*/ _jsx(WithStatusIcon, {
                        testId: "bill-service-table-rate-warning-message",
                        info: (error === null || error === void 0 ? void 0 : error.message) === maxError ? billTexts.cannotInputMoreThanAmount({
                            amount: utils.formatDollarAmount(maxPrice)
                        }) : isWarning ? billTexts.paymentAboveMaxRequiresApproval({
                            amount: utils.formatDollarAmount(cost.maxPrice)
                        }) : billTexts.enterPositiveNumber(),
                        statusType: error ? 'error' : 'warning',
                        size: "medium"
                    })
                }));
            }
        });
    const rowItems = compact([
        {
            value: /*#__PURE__*/ _jsx(Typography, {
                ellipsis: true,
                testId: "bill-service-".concat(itemIndex, "-table-header-name"),
                children: fullService.name
            })
        },
        {
            value: /*#__PURE__*/ _jsx("div", {
                className: styles.quantityCell,
                children: isVariablePrice && /*#__PURE__*/ _jsx(Controller, {
                    control: control,
                    name: "items.".concat(itemIndex, ".service.quantity"),
                    rules: {
                        validate: (quantity)=>{
                            if (!quantity) return requiredError;
                            const asNumber = new Decimal(+quantity);
                            if (asNumber.lessThanOrEqualTo(0)) return requiredError;
                            if (asNumber.greaterThan(maxQuantity)) return maxError;
                        }
                    },
                    render: (renderProps)=>{
                        const { fieldState: { error } } = renderProps;
                        const isWarning = !!quantityWarning && !error;
                        return /*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                            bi: {
                                name: 'service_quantity'
                            },
                            collapseHelperText: false,
                            isMoney: true,
                            size: "small",
                            width: 130,
                            inputProps: {
                                min: 0,
                                className: styles.amountCell
                            }
                        }, connectControllerToTextField(renderProps, {
                            noHelperText: true,
                            alwaysShowErrors: true
                        })), {
                            testId: "bill-service-".concat(itemIndex, "-table-quantity"),
                            warning: isWarning,
                            suffix: (error || !!quantityWarning) && /*#__PURE__*/ _jsx(WithStatusIcon, {
                                testId: "bill-service-".concat(itemIndex, "-table-quantity-warning-message"),
                                "data-intent": error ? maxError : quantityWarning || '',
                                info: (error === null || error === void 0 ? void 0 : error.message) === maxError ? billTexts.cannotInputMoreThan({
                                    count: utils.formatNumberWithCommas(maxQuantity),
                                    unit: serviceCostModel.pluralizedUnitName(cost, maxQuantity.toNumber(), costDisplayTexts)
                                }) : quantityWarning ? qtyWarningToText[quantityWarning] : billTexts.enterPositiveNumber(),
                                statusType: isWarning ? 'warning' : 'error',
                                size: "medium"
                            })
                        }));
                    }
                })
            })
        },
        {
            value: isRangePrice ? getRangeController(cost) : /*#__PURE__*/ _jsxs("div", {
                className: styles.rateWithUnit,
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        testId: "bill-service-table-rate-display",
                        children: utils.formatDollarAmount(cost.price)
                    }),
                    isVariablePrice && /*#__PURE__*/ _jsxs(Typography, {
                        ellipsis: true,
                        variant: "paragraph2",
                        skin: "secondary",
                        children: [
                            "/",
                            serviceCostModel.formattedUnitName(cost, costDisplayTexts)
                        ]
                    })
                ]
            })
        },
        {
            value: cost.discount ? /*#__PURE__*/ _jsx(Typography, {
                align: "right",
                children: cost.discount.format()
            }) : null
        },
        isVariablePrice ? {
            value: cost.unitCap ? /*#__PURE__*/ _jsx(Typography, {
                align: "right",
                testId: "bill-service-table-preapproved-units",
                children: utils.formatNumberWithCommas(cost.unitCap)
            }) : null
        } : isRangePrice ? {
            value: /*#__PURE__*/ _jsx(Typography, {
                align: "right",
                testId: "bill-service-table-rate-range",
                children: utils.getPriceRangeText(cost)
            })
        } : {
            value: null
        },
        {
            value: /*#__PURE__*/ _jsx(Typography, {
                align: "right",
                testId: "bill-service-table-total",
                children: utils.formatDollarAmount(total)
            })
        }
    ]);
    return /*#__PURE__*/ _jsx(BillTableItem, {
        columnDefs: columnDefs,
        headerItems: headerItems,
        rowItems: rowItems,
        headerTestId: "bill-service-table-header",
        actionColumn: actionColumn,
        footer: /*#__PURE__*/ _jsxs("div", {
            className: styles.noteAndCalloutsContainer,
            children: [
                /*#__PURE__*/ _jsx(BillingItemNote, {
                    testId: "manual-bill-note-".concat(itemIndex),
                    name: "items.".concat(itemIndex, ".note")
                }),
                /*#__PURE__*/ _jsx(CumulativeWarningCallout, {
                    service: fullService,
                    issueDate: issueDate,
                    manualServiceBilling: manualServiceBilling,
                    relationshipEffectiveDate: relationshipEffectiveDate,
                    testId: "bill-service-cumulative-".concat(itemIndex)
                }),
                /*#__PURE__*/ _jsx(ServicePendingAmendmentCallout, {
                    service: fullService,
                    testId: "pending-amendment-callout-".concat(itemIndex)
                }),
                /*#__PURE__*/ _jsx(ServiceEffectiveDateCallout, {
                    mode: mode,
                    service: fullService,
                    issueDate: issueDate,
                    relationshipEffectiveDate: relationshipEffectiveDate,
                    testId: "effective-date-callout-".concat(itemIndex)
                })
            ]
        })
    });
}
